import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import Chart from 'react-apexcharts';

function shortDate(value) {
  const parts = String(value || '').substring(0, 10).split('-');
  if (parts.length !== 3) return value || '';
  return `${parts[2]}/${parts[1]}`;
}

function evolutionTickAmount(rowCount, density = 'desktop') {
  if (rowCount <= 1) return 1;
  if (rowCount <= 7) return rowCount - 1;
  if (density === 'narrow') return 3;
  if (density === 'compact') return 4;
  return rowCount <= 30 ? 9 : 11;
}

function csvCell(value) {
  const cleanValue = value === null || typeof value === 'undefined'
    ? ''
    : String(value);
  return `"${cleanValue.replace(/"/g, '""')}"`;
}

export function evolutionDailyCsvRows(rows = []) {
  return [
    [
      'Date',
      'New registrations',
      'Validated registrations',
      'Not validated registrations',
      'Suspended registrations',
      'First-time purchasers',
      'Clients placing 2nd+ order',
      'Active clients',
    ].map(csvCell).join(','),
    ...rows.map((row) => [
      row.date,
      row.new_registrations,
      row.validated_registrations,
      row.not_validated_registrations,
      row.suspended_registrations,
      row.first_time_purchasers,
      row.returning_purchasers,
      row.active_clients,
    ].map(csvCell).join(',')),
  ];
}

const exportPeriods = [
  { value: 'today', label: 'Today (in progress)' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7d', label: 'Last 7 complete days' },
  { value: 'last_30d', label: 'Last 30 complete days' },
  { value: 'last_90d', label: 'Last 90 complete days' },
];

function localTodayIso() {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().substring(0, 10);
}

function inclusiveDateCount(dateFrom, dateTo) {
  const from = Date.parse(`${dateFrom}T00:00:00Z`);
  const to = Date.parse(`${dateTo}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.floor((to - from) / 86400000) + 1;
}

function ClientsEvolutionChart({
  response,
  status,
  onRetry,
  onExportPeriod,
  exportStatus = 'idle',
  externalExportTrigger = false,
  onExportMenuReady,
}) {
  const [chartView, setChartView] = React.useState('registrations');
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState(null);
  const [exportError, setExportError] = React.useState('');
  const [customPeriodOpen, setCustomPeriodOpen] = React.useState(false);
  const [customDateFrom, setCustomDateFrom] = React.useState('');
  const [customDateTo, setCustomDateTo] = React.useState('');
  const [customPeriodError, setCustomPeriodError] = React.useState('');
  const customPeriodPendingRef = React.useRef(false);
  const exportMenuTriggerRef = React.useRef(null);
  const rows = response?.rows || [];
  const period = response?.period || {};
  const maximumExportDate = period.dateTo || localTodayIso();
  const countyFilter = response?.filters?.county || {
    value: 'all',
    label: 'All counties',
  };
  const loading = status === 'loading';
  const error = status === 'error';
  const hasData = Boolean(response);
  const validationView = chartView === 'validation';
  const singleDayView = Number(period.days) === 1
    || period.periodMode === 'today'
    || period.periodMode === 'yesterday';
  const zeroActivity = rows.length > 0 && rows.every((row) => (
    Number(row.new_registrations || 0) === 0
    && Number(row.first_time_purchasers || 0) === 0
    && Number(row.returning_purchasers || 0) === 0
    && Number(row.active_clients || 0) === 0
  ));
  const dateCategories = rows.map((row) => shortDate(row.date));

  const openExportMenu = React.useCallback((anchor) => {
    exportMenuTriggerRef.current = anchor;
    setExportMenuAnchor(anchor);
  }, []);

  React.useEffect(() => {
    if (!onExportMenuReady) return undefined;
    onExportMenuReady(openExportMenu);
    return () => onExportMenuReady(null);
  }, [onExportMenuReady, openExportMenu]);

  const chartOptions = {
    chart: {
      type: validationView ? 'bar' : 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      selection: { enabled: false },
      animations: { enabled: true, speed: 300 },
      fontFamily: 'inherit',
      stacked: validationView,
    },
    colors: validationView
      ? ['#059669', '#d97706', '#64748b']
      : ['#1a237e', '#00897b'],
    dataLabels: { enabled: false },
    stroke: {
      width: validationView ? [0, 0, 0] : [0, 3],
      curve: 'smooth',
    },
    markers: {
      size: validationView ? 0 : [0, singleDayView ? 8 : 0],
      strokeWidth: !validationView && singleDayView ? 3 : 0,
      strokeColors: '#ffffff',
      hover: { sizeOffset: 2 },
    },
    plotOptions: {
      bar: { borderRadius: 4, columnWidth: rows.length > 45 ? '72%' : '58%' },
    },
    fill: { opacity: validationView ? [0.94, 0.94, 0.94] : [0.92, 1] },
    grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
      fontWeight: 600,
      markers: { radius: 12 },
    },
    xaxis: {
      categories: dateCategories,
      tickAmount: evolutionTickAmount(rows.length),
      tickPlacement: 'on',
      labels: {
        show: true,
        rotate: 0,
        rotateAlways: false,
        trim: false,
        hideOverlappingLabels: false,
        showDuplicates: false,
        maxHeight: 32,
        style: { colors: '#64748b', fontSize: '11px' },
      },
      axisBorder: { color: '#cbd5e1' },
      axisTicks: { color: '#cbd5e1' },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (value) => Math.round(value).toLocaleString('ro-RO'),
        style: { colors: '#64748b' },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (value, context) => rows[context.dataPointIndex]?.date || value,
      },
      y: { formatter: (value) => `${Number(value || 0).toLocaleString('ro-RO')} clients` },
    },
    noData: { text: 'No client activity in this period' },
    responsive: [
      {
        breakpoint: 700,
        options: {
          xaxis: {
            categories: dateCategories,
            tickAmount: evolutionTickAmount(rows.length, 'compact'),
            tickPlacement: 'on',
            labels: {
              show: true,
              rotate: 0,
              rotateAlways: false,
              trim: false,
              hideOverlappingLabels: false,
              showDuplicates: false,
              maxHeight: 28,
              style: { colors: '#64748b', fontSize: '10px' },
            },
          },
        },
      },
      {
        breakpoint: 420,
        options: {
          xaxis: {
            categories: dateCategories,
            tickAmount: evolutionTickAmount(rows.length, 'narrow'),
            tickPlacement: 'on',
            labels: {
              show: true,
              rotate: 0,
              rotateAlways: false,
              trim: false,
              hideOverlappingLabels: false,
              showDuplicates: false,
              maxHeight: 26,
              style: { colors: '#64748b', fontSize: '10px' },
            },
          },
        },
      },
    ],
  };

  const series = validationView
    ? [
      {
        name: 'Validated',
        data: rows.map((row) => Number(row.validated_registrations || 0)),
      },
      {
        name: 'Not validated',
        data: rows.map((row) => Number(row.not_validated_registrations || 0)),
      },
      {
        name: 'Suspended',
        data: rows.map((row) => Number(row.suspended_registrations || 0)),
      },
    ]
    : [
      {
        name: 'New registrations',
        type: 'column',
        data: rows.map((row) => Number(row.new_registrations || 0)),
      },
      {
        name: 'Placed 1st order',
        type: 'line',
        data: rows.map((row) => Number(row.first_time_purchasers || 0)),
      },
    ];

  const handleExport = (exportResponse) => {
    const exportRows = exportResponse?.rows || [];
    const exportPeriod = exportResponse?.period || {};
    const exportCounty = exportResponse?.filters?.county || countyFilter;
    const exportSummary = exportResponse?.summary || {};
    if (!exportRows.length) return;

    const total = exportSummary.total_clients || {};
    const registrations = exportSummary.new_registrations || {};
    const active = exportSummary.active_clients || {};
    const firstTime = exportSummary.first_time_purchasers || {};
    const returning = exportSummary.returning_clients || {};
    const lines = [
      ['Clients evolution'].map(csvCell).join(','),
      ['Period mode', exportPeriod.periodMode].map(csvCell).join(','),
      ['Client profile county', exportCounty.label].map(csvCell).join(','),
      ['Date from', exportPeriod.dateFrom].map(csvCell).join(','),
      ['Date to', exportPeriod.dateTo].map(csvCell).join(','),
      ['Timezone', exportPeriod.timezone].map(csvCell).join(','),
      ['Qualifying order statuses', 'Complete; Complete with errors; Paid'].map(csvCell).join(','),
      ['Generated at', exportResponse.generated_at || new Date().toISOString()].map(csvCell).join(','),
      ['Definitions version', exportResponse.definitions_version].map(csvCell).join(','),
      '',
      ['Metric', 'Current', 'Previous'].map(csvCell).join(','),
      ['Total clients', total.value, ''].map(csvCell).join(','),
      ['New registrations', registrations.value, registrations.previous].map(csvCell).join(','),
      ['Active clients', active.value, active.previous].map(csvCell).join(','),
      ['First-time purchasers', firstTime.value, firstTime.previous].map(csvCell).join(','),
      ['Returning purchasers', returning.value, returning.previous].map(csvCell).join(','),
      ['Returning purchasers share of active %', returning.share_of_active, returning.previous_share_of_active].map(csvCell).join(','),
      ['Returning definition', 'A second or later lifetime purchase in the selected period; first-time and returning counts can overlap'].map(csvCell).join(','),
      '',
      ...evolutionDailyCsvRows(exportRows),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = [
      'clients_evolution',
      exportPeriod.periodMode || 'period',
      exportPeriod.dateFrom || 'from',
      exportPeriod.dateTo || 'to',
      exportCounty.value || 'all',
    ].join('_') + '.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePeriodExport = async (periodSelection) => {
    setExportMenuAnchor(null);
    setExportError('');
    try {
      const useCurrentResponse = typeof periodSelection === 'string'
        && periodSelection === period.periodMode;
      const exportResponse = useCurrentResponse
        ? response
        : await onExportPeriod(periodSelection);
      handleExport(exportResponse);
    } catch (error) {
      if (!error?.aborted) {
        setExportError('The selected export period could not be loaded. Please retry.');
      }
    }
  };

  const openCustomPeriod = () => {
    customPeriodPendingRef.current = true;
    setExportMenuAnchor(null);
  };

  const handleExportMenuExited = () => {
    if (!customPeriodPendingRef.current) return;
    customPeriodPendingRef.current = false;
    if (exportMenuTriggerRef.current?.focus) {
      exportMenuTriggerRef.current.focus();
    }
    setCustomDateFrom(period.dateFrom || '');
    setCustomDateTo(maximumExportDate);
    setCustomPeriodError('');
    setCustomPeriodOpen(true);
  };

  const handleCustomPeriodExport = () => {
    if (!customDateFrom || !customDateTo) {
      setCustomPeriodError('Choose both a start date and an end date.');
      return;
    }
    if (customDateFrom > customDateTo) {
      setCustomPeriodError('The start date must be on or before the end date.');
      return;
    }
    if (customDateFrom > maximumExportDate || customDateTo > maximumExportDate) {
      setCustomPeriodError('Custom export dates cannot be in the future.');
      return;
    }
    const dayCount = inclusiveDateCount(customDateFrom, customDateTo);
    if (!dayCount || dayCount > 366) {
      setCustomPeriodError('Choose a period of no more than 366 days.');
      return;
    }

    setCustomPeriodOpen(false);
    handlePeriodExport({
      periodMode: 'custom',
      dateFrom: customDateFrom,
      dateTo: customDateTo,
    });
  };

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
      }}
    >
      {loading && hasData && <LinearProgress aria-label="Refreshing client evolution" />}
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.25 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography sx={{ color: '#111827', fontWeight: 800, fontSize: '1.05rem' }}>
              Client evolution
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.8rem', mt: 0.2 }}>
              {validationView
                ? `Current validation status by registration date · ${countyFilter.label}`
                : `Daily registrations and clients placing their 1st order · ${countyFilter.label}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={chartView}
              onChange={(event, value) => value && setChartView(value)}
              aria-label="Client evolution chart view"
              sx={{
                flex: { xs: 1, sm: 'initial' },
                '& .MuiToggleButton-root': {
                  flex: { xs: 1, sm: 'initial' },
                  px: { xs: 0.8, sm: 1.25 },
                  py: 0.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.68rem', sm: '0.74rem' },
                },
              }}
            >
              <ToggleButton value="registrations">Registrations</ToggleButton>
              <ToggleButton value="validation">Validation</ToggleButton>
            </ToggleButtonGroup>
            <Button
              size="small"
              variant="outlined"
              startIcon={exportStatus === 'loading' ? <CircularProgress size={15} /> : <DownloadRoundedIcon />}
              endIcon={<KeyboardArrowDownRoundedIcon />}
              disabled={!hasData || !rows.length || exportStatus === 'loading'}
              onClick={(event) => openExportMenu(event.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(exportMenuAnchor)}
              sx={{
                display: externalExportTrigger ? { xs: 'inline-flex', sm: 'none' } : 'inline-flex',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {exportStatus === 'loading' ? 'Preparing…' : 'Export CSV'}
            </Button>
          </Stack>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
            MenuListProps={{ 'aria-label': 'CSV export period' }}
            TransitionProps={{ onExited: handleExportMenuExited }}
          >
            {exportPeriods.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === period.periodMode}
                onClick={() => handlePeriodExport(option.value)}
              >
                <ListItemText
                  primary={option.label}
                  secondary={option.value === period.periodMode ? 'Current dashboard period' : null}
                />
              </MenuItem>
            ))}
            <MenuItem onClick={openCustomPeriod}>
              <ListItemText
                primary="Custom period…"
                secondary="Choose a start and end date"
              />
            </MenuItem>
          </Menu>
        </Stack>

        <Dialog
          open={customPeriodOpen}
          onClose={() => setCustomPeriodOpen(false)}
          fullWidth
          maxWidth="xs"
          aria-labelledby="custom-client-export-title"
        >
          <DialogTitle id="custom-client-export-title">
            Export custom period
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#64748b', fontSize: '0.8rem', mb: 1.5 }}>
              Choose up to 366 days. This export will use {countyFilter.label} and will not change the dashboard.
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Start date"
                type="date"
                value={customDateFrom}
                onChange={(event) => {
                  setCustomDateFrom(event.target.value);
                  setCustomPeriodError('');
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: customDateTo || maximumExportDate }}
              />
              <TextField
                label="End date"
                type="date"
                value={customDateTo}
                onChange={(event) => {
                  setCustomDateTo(event.target.value);
                  setCustomPeriodError('');
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customDateFrom || undefined, max: maximumExportDate }}
              />
              {customPeriodError && (
                <Alert severity="warning" role="alert">
                  {customPeriodError}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCustomPeriodOpen(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomPeriodExport}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Export CSV
            </Button>
          </DialogActions>
        </Dialog>

        {exportError && (
          <Alert severity="warning" onClose={() => setExportError('')} sx={{ mt: 1.25 }}>
            {exportError}
          </Alert>
        )}

        {error && !hasData ? (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={onRetry}>
                Retry
              </Button>
            )}
            sx={{ mt: 2 }}
          >
            Client evolution could not be loaded.
          </Alert>
        ) : loading && !hasData ? (
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rounded" height={330} />
          </Box>
        ) : (
          <Box
            role="img"
            aria-label={`Client evolution ${validationView ? 'validation status' : 'registrations and first orders'} for ${period.label || 'the selected period'} and ${countyFilter.label}`}
            sx={{ mt: 1.5, minHeight: 330, touchAction: 'pan-y' }}
          >
            <Chart
              key={chartView}
              options={chartOptions}
              series={series}
              type={validationView ? 'bar' : 'line'}
              height={340}
            />
            {zeroActivity && (
              <Typography align="center" sx={{ color: '#64748b', fontSize: '0.8rem', mt: -1 }}>
                No client activity in this period.
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ClientsEvolutionChart;
